// ═══════════════════════════════════════════════════════════════════════════════
// Task Validation Schemas
// ═══════════════════════════════════════════════════════════════════════════════
import { z } from 'zod';
import { APPROVAL_STATUS, LEVEL1_DEFAULTS, SUPPORTED_LANGUAGES } from '../constants/index.js';
import { dateSchema, uuidSchema, optionalTimeSchema, positiveIntSchema } from './common.schema.js';
import { DATETIME_REGEX } from '../constants/index.js';
import { monthlyKindSchema, recurrenceDaysSchema, recurrenceMonthDaysSchema, recurrenceOrdinalSchema, recurrenceTypeSchema, refineOccurrenceOrStartDates, refineWeekdaySelection, } from './recurrence.schema.js';
const taskDateRangeSchema = z.object({
    startDate: dateSchema,
    endDate: dateSchema,
}).refine((data) => data.startDate <= data.endDate, {
    message: 'Range end date must be on or after start date',
    path: ['endDate'],
});
// ── Create Task Input Schema ──
export const createTaskInputSchema = z.object({
    title: z.string()
        .min(1, 'Title is required')
        .max(100, 'Title must not exceed 100 characters')
        .transform((val) => val.trim()),
    description: z.string()
        .max(1000, 'Description must not exceed 1000 characters')
        .default('')
        .transform((val) => val.trim()),
    recurrence: recurrenceTypeSchema.default('none'),
    recurrenceDays: recurrenceDaysSchema,
    monthlyKind: monthlyKindSchema.nullable().default(null),
    recurrenceMonthDays: recurrenceMonthDaysSchema,
    recurrenceOrdinal: recurrenceOrdinalSchema,
    occurrenceDate: z.string().default(''),
    startDate: z.string().default(''),
    endDate: z.string().default(''),
    exclusionRanges: z.array(taskDateRangeSchema).max(100).default([]),
    dueTime: optionalTimeSchema.default(''),
    requirePhoto: z.boolean().default(true),
    notifyEmployer: z.boolean().default(true),
    remindHelperBeforeDue: z.boolean().default(false),
}).superRefine((data, ctx) => {
    refineWeekdaySelection(data, ctx);
    refineOccurrenceOrStartDates(data, ctx);
});
/** JSON may send GPS numbers as strings; coerce before range checks */
const preprocessGpsCoordinate = (val) => {
    if (val === '' || val === null || val === undefined)
        return '';
    if (typeof val === 'number')
        return val;
    if (typeof val === 'string') {
        const trimmed = val.trim();
        if (trimmed === '')
            return '';
        const n = Number(trimmed);
        if (Number.isFinite(n))
            return n;
    }
    return val;
};
// ── Update Task Input Schema ──
export const updateTaskInputSchema = z.object({
    title: z.string()
        .min(1, 'Title is required')
        .max(100, 'Title must not exceed 100 characters')
        .transform((val) => val.trim())
        .optional(),
    description: z.string()
        .max(1000, 'Description must not exceed 1000 characters')
        .transform((val) => val.trim())
        .optional(),
    recurrence: recurrenceTypeSchema.optional(),
    recurrenceDays: recurrenceDaysSchema.optional(),
    monthlyKind: monthlyKindSchema.nullable().optional(),
    recurrenceMonthDays: recurrenceMonthDaysSchema.optional(),
    recurrenceOrdinal: recurrenceOrdinalSchema.optional(),
    occurrenceDate: z.union([dateSchema, z.literal('')]).optional(),
    startDate: z.union([dateSchema, z.literal('')]).optional(),
    endDate: z.union([dateSchema, z.literal('')]).optional(),
    exclusionRanges: z.array(taskDateRangeSchema).max(100).optional(),
    dueTime: optionalTimeSchema.optional(),
    requirePhoto: z.boolean().optional(),
    notifyEmployer: z.boolean().optional(),
    remindHelperBeforeDue: z.boolean().optional(),
    /** Only for `taskKind === "inventory"`. Changes which checklist template this stock take uses. */
    inventoryListId: uuidSchema.optional(),
    /** Wizard v2: stock take categories, locations, expiry lead days (employer edit). */
    categories: z.array(z.string().min(1).max(120).transform((s) => s.trim())).max(100).optional(),
    locations: z.array(z.string().min(1).max(150).transform((s) => s.trim())).max(100).optional(),
    notifyExpiryLeadDays: z
        .array(z.number().int().refine((v) => [3, 7, 10, 14, 30].includes(v), 'Invalid lead day'))
        .max(5)
        .optional(),
}).superRefine((data, ctx) => {
    if (!data.recurrence)
        return;
    refineWeekdaySelection({
        recurrence: data.recurrence,
        recurrenceDays: data.recurrenceDays ?? [],
        monthlyKind: data.monthlyKind,
        recurrenceMonthDays: data.recurrenceMonthDays ?? [],
        recurrenceOrdinal: data.recurrenceOrdinal ?? null,
        occurrenceDate: data.occurrenceDate ?? '',
        startDate: data.startDate ?? '',
        endDate: data.endDate ?? '',
    }, ctx);
    if (data.recurrence === 'none') {
        if (data.occurrenceDate === undefined || data.occurrenceDate === '') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'One-time task requires occurrence date',
                path: ['occurrenceDate'],
            });
        }
        return;
    }
    if (data.startDate === undefined || data.startDate === '') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Recurring task requires start date',
            path: ['startDate'],
        });
    }
});
// ── Submit Task Input Schema ──
export const submitTaskInputSchema = z.object({
    photoUrls: z.array(z.string().url('Invalid photo URL'))
        .min(0)
        .max(LEVEL1_DEFAULTS.MAX_TASK_PHOTOS, `Maximum ${LEVEL1_DEFAULTS.MAX_TASK_PHOTOS} photos allowed`),
    gpsLat: z.preprocess(preprocessGpsCoordinate, z.union([z.number().min(-90).max(90), z.literal('')])).default(''),
    gpsLng: z.preprocess(preprocessGpsCoordinate, z.union([z.number().min(-180).max(180), z.literal('')])).default(''),
    gpsEnabled: z.boolean().default(false),
    note: z.string()
        .max(500, 'Note must not exceed 500 characters')
        .default('')
        .transform((val) => val.trim()),
    completedAt: z.string().regex(DATETIME_REGEX, 'completedAt must be YYYY-MM-DD HH:mm'),
}).refine((data) => {
    // If GPS enabled, both lat and lng must be provided
    if (data.gpsEnabled) {
        return data.gpsLat !== '' && data.gpsLng !== '';
    }
    return true;
}, { message: 'GPS coordinates are required when GPS is enabled', path: ['gpsLat'] });
// ── Review Task Input Schema ──
export const reviewTaskInputSchema = z.object({
    status: z.enum([APPROVAL_STATUS.APPROVED, APPROVAL_STATUS.NEEDS_UPDATE], {
        error: 'Status must be approved or needs_update',
    }),
    version: positiveIntSchema,
    needsUpdateReason: z
        .string()
        .max(500, 'needsUpdateReason must not exceed 500 characters')
        .optional()
        .default('')
        .transform((val) => val.trim()),
});
// ── Cancel Task Input Schema ──
export const cancelTaskInputSchema = z.object({
    taskInstanceId: uuidSchema,
});
// ── Task ID Param Schema ──
export const taskIdParamSchema = z.object({
    id: uuidSchema,
});
// ── Task Instance ID Param Schema ──
export const taskInstanceIdParamSchema = z.object({
    id: uuidSchema,
});
/** Optional viewer UI language for task detail / todo list translations. */
export const taskViewerLanguageQuerySchema = z.object({
    viewerLanguage: z.enum(SUPPORTED_LANGUAGES).optional(),
});
// ── Task instance photo stream (GET .../:id/photos/:photoIndex) ──
export const taskInstancePhotoParamsSchema = z.object({
    id: uuidSchema,
    photoIndex: z.coerce.number().int().min(0).max(LEVEL1_DEFAULTS.MAX_TASK_PHOTOS - 1),
});
/** Inventory v2 wizard: stream one location photo (GET .../inventory/v2/locations/:locationIndex/photos/:photoIndex) */
export const taskInstanceInventoryV2LocationPhotoParamsSchema = z.object({
    id: uuidSchema,
    locationIndex: z.coerce.number().int().min(0).max(LEVEL1_DEFAULTS.MAX_INVENTORY_LIST_ITEMS - 1),
    photoIndex: z.coerce.number().int().min(0).max(99),
});
/** Deduped stocktake line photos (GET .../inventory/stocktake-photos/:photoIndex) — index aligns with TaskReviewPage */
export const taskInstanceInventoryStocktakePhotoParamsSchema = z.object({
    id: uuidSchema,
    photoIndex: z.coerce.number().int().min(0).max(LEVEL1_DEFAULTS.MAX_INVENTORY_LIST_ITEMS - 1),
});
//# sourceMappingURL=task.schema.js.map